import React, {useEffect, useMemo, useState} from "react";

import {RangeSlider} from "components/ClientHome/CalculatorComponents";
import {
  calculateSipLumpsumPlan,
  DEFAULT_INPUT,
  INPUT_LIMITS,
  snapToStep
} from "./CalculatorLogic";
import InflationSlider from "components/Calculators/InflationSlider";
import {
  DonutChartSection,
  MobileTabToggle,
  SummaryCards,
  ViewToggle,
  YearlyTable
} from "./CalculatorComponents";
import {colors} from "nxtconstants";
import {
  CalculatorSection,
  CalculatorWrapper,
  ControlPanel,
  PageTitle,
  ResultsPanel
} from "./CalculatorStyle";

const GoalPlanningSipLumpsumCalculator = () => {
  const [targetCorpus, setTargetCorpus] = useState(DEFAULT_INPUT.targetCorpus);
  const [targetPeriod, setTargetPeriod] = useState(DEFAULT_INPUT.targetPeriod);
  const [returnRate, setReturnRate] = useState(DEFAULT_INPUT.returnRate);
  const [sipPeriod, setSipPeriod] = useState(DEFAULT_INPUT.sipPeriod);
  const [stepUp, setStepUp] = useState(DEFAULT_INPUT.stepUp);
  const [lumpsumAmount, setLumpsumAmount] = useState(DEFAULT_INPUT.lumpsumAmount);
  const [inflation, setInflation] = useState(DEFAULT_INPUT.inflation);
  const [isInflation, setIsInflation] = useState(DEFAULT_INPUT.isInflation);
  const [activeView, setActiveView] = useState("graph");
  const [mobileTab, setMobileTab] = useState("input");
  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    let timeoutId;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setChartKey((previous) => previous + 1);
      }, 150);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const result = useMemo(
    () =>
      calculateSipLumpsumPlan({
        targetCorpus,
        targetPeriod,
        returnRate,
        sipPeriod,
        stepUp,
        lumpsumAmount,
        inflation,
        isInflation
      }),
    [
      targetCorpus,
      targetPeriod,
      returnRate,
      sipPeriod,
      stepUp,
      lumpsumAmount,
      inflation,
      isInflation
    ]
  );

  const sliderProps = {
    accentColor: colors.primary,
    showRangeLabels: false
  };

  return (
    <CalculatorSection>
      <PageTitle>Goal Planning SIP + Lumpsum Calculator</PageTitle>

      <MobileTabToggle activeTab={mobileTab} onChange={setMobileTab} />

      <CalculatorWrapper>
        <ControlPanel $hidden={mobileTab === "result"}>
          <RangeSlider
            label="Target Corpus"
            value={targetCorpus}
            onChange={(value) =>
              setTargetCorpus(snapToStep(value, INPUT_LIMITS.targetCorpus.step))
            }
            min={INPUT_LIMITS.targetCorpus.min}
            max={INPUT_LIMITS.targetCorpus.max}
            step={INPUT_LIMITS.targetCorpus.step}
            prefix="₹"
            {...sliderProps}
          />

          <RangeSlider
            label="Time to Reach Target"
            value={targetPeriod}
            onChange={setTargetPeriod}
            min={INPUT_LIMITS.targetPeriod.min}
            max={INPUT_LIMITS.targetPeriod.max}
            step={INPUT_LIMITS.targetPeriod.step}
            suffix=" Years"
            {...sliderProps}
          />

          <RangeSlider
            label="Expected Return Rate"
            value={returnRate}
            onChange={setReturnRate}
            min={INPUT_LIMITS.returnRate.min}
            max={INPUT_LIMITS.returnRate.max}
            step={INPUT_LIMITS.returnRate.step}
            suffix="%"
            {...sliderProps}
          />

          <RangeSlider
            label="SIP Period"
            value={sipPeriod}
            onChange={setSipPeriod}
            min={INPUT_LIMITS.sipPeriod.min}
            max={INPUT_LIMITS.sipPeriod.max}
            step={INPUT_LIMITS.sipPeriod.step}
            suffix=" Years"
            {...sliderProps}
          />

          <RangeSlider
            label="Annual Step-Up"
            value={stepUp}
            onChange={setStepUp}
            min={INPUT_LIMITS.stepUp.min}
            max={INPUT_LIMITS.stepUp.max}
            step={INPUT_LIMITS.stepUp.step}
            suffix="%"
            {...sliderProps}
          />

          <RangeSlider
            label="Lumpsum Investment"
            value={lumpsumAmount}
            onChange={(value) =>
              setLumpsumAmount(
                snapToStep(value, INPUT_LIMITS.lumpsumAmount.step)
              )
            }
            min={INPUT_LIMITS.lumpsumAmount.min}
            max={INPUT_LIMITS.lumpsumAmount.max}
            step={INPUT_LIMITS.lumpsumAmount.step}
            prefix="₹"
            {...sliderProps}
          />

          <InflationSlider
            value={inflation}
            onChange={setInflation}
            isInflation={isInflation}
            onToggle={() => setIsInflation((previous) => !previous)}
            min={INPUT_LIMITS.inflation.min}
            max={INPUT_LIMITS.inflation.max}
            step={INPUT_LIMITS.inflation.step}
            accentColor={colors.primary}
          />
        </ControlPanel>

        <ResultsPanel $hidden={mobileTab === "input"}>
          <SummaryCards
            adjustedTarget={result.adjustedTarget}
            initialSip={result.initialSip}
            targetPeriod={targetPeriod}
          />

          <ViewToggle activeView={activeView} onChange={setActiveView} />

          {activeView === "graph" ? (
            <DonutChartSection
              chartKey={chartKey}
              gainPercent={result.gainPercent}
              investedPercent={result.investedPercent}
              totalGain={result.totalGain}
              totalInvested={result.totalInvested}
            />
          ) : (
            <YearlyTable yearlyData={result.returnData} />
          )}
        </ResultsPanel>
      </CalculatorWrapper>
    </CalculatorSection>
  );
};

export default GoalPlanningSipLumpsumCalculator;
